import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommunity, REPORT_STATUS } from '../contexts/CommunityContext';
import { useNotification, NOTIFICATION_TYPES } from '../contexts/NotificationContext';
import { Card, StatCard, Button, StatusBadge, ConfirmModal } from '../components/common';

const Reports = () => {
  const { t } = useTranslation();
  const { getReports, handleReport, getHiddenPosts } = useCommunity();
  const { addNotification } = useNotification();

  const [reports, setReports] = useState([]);
  const [hiddenPosts, setHiddenPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [actionModal, setActionModal] = useState({ open: false, report: null, action: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [allReports, hidden] = await Promise.all([
        getReports(),
        Promise.resolve(getHiddenPosts())
      ]);
      setReports(allReports || []);
      setHiddenPosts(hidden || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  }, [getReports, getHiddenPosts]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleActionClick = (report, action) => {
    setActionModal({ open: true, report, action });
  };

  const handleActionConfirm = async () => {
    const { report, action } = actionModal;
    if (!report) return;

    setActionLoading(true);
    try {
      handleReport(report.id, action, true);
      setMessage({
        text: t('admin.moderation.success.' + action),
        type: 'success'
      });
      setActionModal({ open: false, report: null, action: '' });
      await loadData();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);

      // 신고자에게 알림 전송
      if (report.reporter) {
        addNotification(
          report.reporter.id,
          NOTIFICATION_TYPES.REPORT_RESULT,
          t('notification.messages.reportProcessed'),
          action === 'hide'
            ? t('admin.moderation.success.hide')
            : t('admin.moderation.success.dismiss'),
          '/community'
        );
      }
    } catch (err) {
      setMessage({
        text: t('admin.moderation.error.actionFailed'),
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case REPORT_STATUS.PENDING: return 'warning';
      case REPORT_STATUS.REVIEWED: return 'info';
      case REPORT_STATUS.DISMISSED: return 'neutral';
      case REPORT_STATUS.ACTIONED: return 'success';
      default: return 'neutral';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      [REPORT_STATUS.PENDING]: '대기중',
      [REPORT_STATUS.REVIEWED]: '검토됨',
      [REPORT_STATUS.DISMISSED]: '기각',
      [REPORT_STATUS.ACTIONED]: '처리됨'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 통계
  const stats = {
    pending: reports.filter(r => r.status === REPORT_STATUS.PENDING).length,
    total: reports.length,
    hidden: hiddenPosts.length,
    actioned: reports.filter(r => r.status === REPORT_STATUS.ACTIONED).length
  };

  // 필터링
  const filteredReports = reports.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    return true;
  });

  const getActionModalTitle = () => {
    return actionModal.action === 'hide' ? '게시물 숨김' : '신고 기각';
  };

  const getActionModalMessage = () => {
    const { report, action } = actionModal;
    if (!report) return '';

    if (action === 'hide') {
      return `"${report.type === 'post' ? report.postTitle : report.commentContent}"를 숨기시겠습니까? 작성자에게 알림이 전송됩니다.`;
    }
    return '이 신고를 기각하시겠습니까? 신고자에게 처리 결과가 전송됩니다.';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="text-gray-500 text-sm">신고 목록을 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1200px] mx-auto max-md:gap-4">
      {/* 메시지 */}
      {message.text && (
        <div className={`py-3 px-4 rounded-lg text-sm font-medium ${
          message.type === 'success'
            ? 'bg-green-100 text-green-600'
            : 'bg-red-100 text-red-600'
        }`}>
          {message.text}
        </div>
      )}

      {/* 헤더 */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-[28px] font-bold text-[#1d1d1f] m-0 mb-1 max-md:text-2xl">신고 관리</h1>
          <p className="text-sm text-gray-500 m-0">커뮤니티 신고 내역을 관리합니다</p>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-md:gap-3 max-sm:grid-cols-1">
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${
            statusFilter === REPORT_STATUS.PENDING
              ? 'bg-amber-500 border-amber-600'
              : 'bg-white border-gray-200'
          }`}
          onClick={() => setStatusFilter(statusFilter === REPORT_STATUS.PENDING ? 'all' : REPORT_STATUS.PENDING)}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${
            statusFilter === REPORT_STATUS.PENDING ? 'grayscale brightness-[10]' : ''
          }`}>⚠️</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${
              statusFilter === REPORT_STATUS.PENDING ? 'text-white' : 'text-amber-500'
            }`}>{stats.pending}</span>
            <span className={`text-[13px] ${
              statusFilter === REPORT_STATUS.PENDING ? 'text-white' : 'text-gray-500'
            }`}>{t('admin.moderation.stats.pending')}</span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${
            statusFilter === 'all'
              ? 'bg-blue-500 border-blue-600'
              : 'bg-white border-gray-200'
          }`}
          onClick={() => setStatusFilter('all')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${
            statusFilter === 'all' ? 'grayscale brightness-[10]' : ''
          }`}>📋</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${
              statusFilter === 'all' ? 'text-white' : 'text-[#1d1d1f]'
            }`}>{stats.total}</span>
            <span className={`text-[13px] ${
              statusFilter === 'all' ? 'text-white' : 'text-gray-500'
            }`}>{t('admin.moderation.stats.total')}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 p-5 bg-white border border-gray-200 rounded-xl cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3">
          <span className="text-[28px] leading-none flex-shrink-0 max-md:text-2xl">🚫</span>
          <div className="flex flex-col gap-0.5">
            <span className="text-2xl font-bold text-[#1d1d1f] leading-tight max-md:text-xl">{stats.hidden}</span>
            <span className="text-[13px] text-gray-500">{t('admin.moderation.stats.hidden')}</span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${
            statusFilter === REPORT_STATUS.ACTIONED
              ? 'bg-green-500 border-green-600'
              : 'bg-white border-gray-200'
          }`}
          onClick={() => setStatusFilter(statusFilter === REPORT_STATUS.ACTIONED ? 'all' : REPORT_STATUS.ACTIONED)}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${
            statusFilter === REPORT_STATUS.ACTIONED ? 'grayscale brightness-[10]' : ''
          }`}>✅</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${
              statusFilter === REPORT_STATUS.ACTIONED ? 'text-white' : 'text-green-500'
            }`}>{stats.actioned}</span>
            <span className={`text-[13px] ${
              statusFilter === REPORT_STATUS.ACTIONED ? 'text-white' : 'text-gray-500'
            }`}>처리 완료</span>
          </div>
        </div>
      </div>

      {/* 필터 */}
      <Card className="bg-white">
        <div className="flex gap-6 flex-wrap max-md:flex-col max-md:gap-4">
          <div className="flex items-center gap-3 max-md:flex-col max-md:items-start max-md:gap-2">
            <span className="text-[13px] font-medium text-gray-500 whitespace-nowrap">상태:</span>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: '전체' },
                { key: REPORT_STATUS.PENDING, label: '대기중' },
                { key: REPORT_STATUS.ACTIONED, label: '처리됨' },
                { key: REPORT_STATUS.DISMISSED, label: '기각' }
              ].map(f => (
                <button
                  key={f.key}
                  className={`py-1.5 px-3 border rounded-md text-xs font-medium cursor-pointer transition-all duration-150 whitespace-nowrap ${
                    statusFilter === f.key
                      ? 'bg-blue-500 border-blue-600 text-white'
                      : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setStatusFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 max-md:flex-col max-md:items-start max-md:gap-2">
            <span className="text-[13px] font-medium text-gray-500 whitespace-nowrap">유형:</span>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: '전체' },
                { key: 'post', label: '게시물' },
                { key: 'comment', label: '댓글' }
              ].map(f => (
                <button
                  key={f.key}
                  className={`py-1.5 px-3 border rounded-md text-xs font-medium cursor-pointer transition-all duration-150 whitespace-nowrap ${
                    typeFilter === f.key
                      ? 'bg-blue-500 border-blue-600 text-white'
                      : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setTypeFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* 신고 목록 */}
      <div className="flex flex-col gap-4">
        {filteredReports.length === 0 ? (
          <Card className="bg-white">
            <div className="flex flex-col items-center py-12 px-6 gap-3">
              <span className="text-5xl leading-none">📭</span>
              <p className="text-sm text-gray-500 m-0">
                {statusFilter === 'all'
                  ? t('admin.moderation.noReports')
                  : '해당 조건의 신고가 없습니다'}
              </p>
            </div>
          </Card>
        ) : (
          filteredReports.map((report) => (
            <Card
              key={report.id}
              className={`bg-white transition-all duration-150 hover:shadow-md ${
                report.status === 'pending'
                  ? 'border-l-[3px] border-l-amber-500'
                  : report.status === 'actioned'
                    ? 'border-l-[3px] border-l-green-500'
                    : report.status === 'dismissed'
                      ? 'border-l-[3px] border-l-gray-400 opacity-80'
                      : ''
              }`}
            >
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-base">
                    {report.type === 'post' ? '📝' : '💬'}
                  </span>
                  <span className="text-xs font-medium text-gray-500">
                    {report.type === 'post' ? t('admin.moderation.type.post') : t('admin.moderation.type.comment')}
                  </span>
                </div>
                <span className={`inline-flex items-center py-1 px-2.5 rounded-full text-[11px] font-medium ${
                  getStatusBadgeVariant(report.status) === 'warning'
                    ? 'bg-amber-100 text-amber-600'
                    : getStatusBadgeVariant(report.status) === 'success'
                      ? 'bg-green-100 text-green-600'
                      : getStatusBadgeVariant(report.status) === 'info'
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-500'
                }`}>
                  {getStatusLabel(report.status)}
                </span>
              </div>

              <div className="mb-3">
                <p className="text-[15px] font-medium text-[#1d1d1f] m-0 mb-2 leading-relaxed">
                  {report.type === 'post' ? report.postTitle : report.commentContent}
                </p>
                <div className="flex gap-2 text-[13px]">
                  <span className="text-gray-400">{t('admin.moderation.reason')}:</span>
                  <span className="text-[#1d1d1f]">{report.reason}</span>
                </div>
              </div>

              <div className="flex gap-6 pt-3 border-t border-gray-100 mb-3 max-md:flex-wrap max-md:gap-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] text-gray-400">{t('admin.moderation.reportedBy')}</span>
                  <span className="text-[13px] font-medium text-[#1d1d1f]">{report.reporter?.name || '-'}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] text-gray-400">{t('admin.moderation.author')}</span>
                  <span className="text-[13px] font-medium text-[#1d1d1f]">{report.author?.name || '-'}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] text-gray-400">신고일</span>
                  <span className="text-[13px] font-medium text-[#1d1d1f]">{formatDate(report.createdAt)}</span>
                </div>
              </div>

              {report.status === REPORT_STATUS.PENDING && (
                <div className="flex gap-2 pt-3 border-t border-gray-100 max-md:flex-col">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleActionClick(report, 'hide')}
                  >
                    {t('admin.moderation.actions.hide')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleActionClick(report, 'dismiss')}
                  >
                    {t('admin.moderation.actions.dismiss')}
                  </Button>
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* 액션 확인 모달 */}
      <ConfirmModal
        isOpen={actionModal.open}
        onClose={() => setActionModal({ open: false, report: null, action: '' })}
        onConfirm={handleActionConfirm}
        title={getActionModalTitle()}
        message={getActionModalMessage()}
        confirmText={actionModal.action === 'hide' ? '숨기기' : '기각'}
        cancelText="취소"
        variant={actionModal.action === 'hide' ? 'danger' : 'primary'}
        loading={actionLoading}
      />
    </div>
  );
};

export default Reports;
